import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Typography, Form, InputNumber, Input, Checkbox, Upload, Divider } from 'antd';
import { InboxOutlined, DeleteOutlined } from '@ant-design/icons';
import { useFlow } from '../contexts/FlowContext';
import initialData from '../mockData.json';
import thresholdsMock from '../thresholds';
import Quote from './Quote';
import productDataMock from '../product_data/index';
import { calculateAnnualOperatingCosts } from './utils';
import { handleFileChange } from './PDFDataMapper';

const { Title } = Typography;
const { Dragger } = Upload;

const extractModelCapacity = (modelName) => {
  const match = modelName.match(/\d+/);
  return match ? parseInt(match[0], 10) : null;
};

const WaterQualityTable = () => {
  const { probableFlow, setProbableFlow } = useFlow();
  const [data, setData] = useState(initialData.map((item, index) => ({
    ...item,
    key: item.id || `data-${index}`,
  })));
  const [analysisData, setAnalysisData] = useState(data);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [thresholdData, setThresholdData] = useState(thresholdsMock);
  const [productData, setProductData] = useState(productDataMock);
  const [showFlowReminder, setShowFlowReminder] = useState(false);
  const [form] = Form.useForm();
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const useMockData = process.env.REACT_APP_USE_MOCK_DATA === 'true';
      if (!useMockData) {
        try {
          const [productResponse, thresholdResponse] = await Promise.all([
            fetch(`${process.env.REACT_APP_API_BASE_URL}/api/Product/get_all_products`, {
              headers: { 'Authentication': localStorage.getItem('userToken') }
            }),
            fetch(`${process.env.REACT_APP_API_BASE_URL}/api/Thresholds/thresholds`, {
              headers: { 'Authentication': localStorage.getItem('userToken') }
            }),
          ]);

          if (!productResponse.ok || !thresholdResponse.ok) {
            throw new Error('Failed to fetch data from the server.');
          }

          const productData = await productResponse.json();
          const thresholds = await thresholdResponse.json();
          setThresholdData(thresholds);
          setProductData(productData);
        } catch (error) {
          console.error('Error fetching data:', error);
          setError('Error fetching data from server.');
        }
      }
    };

    fetchData();
  }, []);

  const getResultValue = (result) => {
    if (typeof result === 'boolean') {
      return result;
    }
    if (typeof result === 'string') {
      if (result.toLowerCase() === 'true') return true;
      if (result.toLowerCase() === 'false') return false;

      const parsed = parseFloat(result.replace(/[<>]/g, '').trim());
      return isNaN(parsed) ? null : parsed;
    }
    return null;
  };

  const isConditionMet = (analysis, resultValue, thresholdValue) => {
    if (analysis === 'pH') {
      return resultValue < thresholdValue;
    }
    if (typeof resultValue === 'boolean' && typeof thresholdValue === 'boolean') {
      return resultValue === thresholdValue;
    }
    return resultValue > thresholdValue;
  };

  const getModelFlowCapacity = (specifications, probableFlow, analysisData) => {
    const intermittentFlow = specifications['Intermittent_l_min'];

    const flowRange = specifications['Flödekapacitet_l_min'];
    if (flowRange) {
      return probableFlow >= flowRange.Low && probableFlow <= flowRange.High;
    }

    let maxFlowBasedOnSpeed = specifications['Vatten_l_min'] || 0;

    if (specifications['Hastighet (m/h)'] || specifications['Hastighet (Bv/h)']) {
      maxFlowBasedOnSpeed = Math.max(
        maxFlowBasedOnSpeed,
        ...Object.values(specifications['Hastighet (m/h)'] || {}),
        ...Object.values(specifications['Hastighet (Bv/h)'] || {})
      );
    }

    if (specifications['fe_mn_ppm'] && analysisData) {
      const feResult = analysisData.find(item => item.Analys === 'Järn (Fe)')?.Resultat || 0;
      const mnResult = analysisData.find(item => item.Analys === 'Mangan (Mn)')?.Resultat || 0;
      const feMnConcentration = parseFloat(feResult) + parseFloat(mnResult);

      for (const interval of specifications['fe_mn_ppm'].intervall) {
        if (feMnConcentration >= interval.min && feMnConcentration <= interval.max) {
          maxFlowBasedOnSpeed = Math.min(maxFlowBasedOnSpeed, interval.flöde);
          break;
        }
      }
    }

    if (specifications['cod_mn_ppm'] && analysisData) {
      const codMnResult = analysisData.find(item => item.Analys === 'COD-Mn')?.Resultat || 0;
      const codMnConcentration = parseFloat(codMnResult);

      for (const interval of specifications['cod_mn_ppm'].intervall) {
        if (codMnConcentration >= interval.min && codMnConcentration <= interval.max) {
          maxFlowBasedOnSpeed = Math.min(maxFlowBasedOnSpeed, interval.flöde);
          break;
        }
      }
    }

    if (specifications['UV-dos J/cm2']) {
      const uvDose300 = specifications['UV-dos J/cm2']['300'];
      const uvDose400 = specifications['UV-dos J/cm2']['400'];
      if (probableFlow <= uvDose400) {
        maxFlowBasedOnSpeed = uvDose400;
      } else if (probableFlow <= uvDose300) {
        maxFlowBasedOnSpeed = uvDose300;
      }
    }

    const headroomPercentage = 0.3;
    const headroomFlow = maxFlowBasedOnSpeed * (1 + headroomPercentage);

    if (intermittentFlow !== undefined && probableFlow > intermittentFlow) {
      return Math.min(headroomFlow, intermittentFlow);
    }

    return Math.min(headroomFlow, maxFlowBasedOnSpeed);
  };

  const totalHardness = data.find(item => item.Analys === 'Hårdhet')?.Resultat;
  const nitrateLevel = data.find(item => item.Analys === 'Nitrat (NO3)')?.Resultat;

  const getNatriumValue = useCallback(() => {
    const natriumAnalysis = analysisData.find(item => item.Analys === 'Natrium (Na)');
    return natriumAnalysis ? parseFloat(natriumAnalysis.Resultat) : null;
  }, [analysisData]);

  const isConductivityProblematic = useCallback(() => {
    const conductivityAnalysis = analysisData.find(item => item.Analys === 'Konduktivitet');
    const conductivityValue = conductivityAnalysis ? parseFloat(conductivityAnalysis.Resultat) : null;
    return conductivityValue !== null && conductivityValue > 50; // TODO: Check if this value is correct
  }, [analysisData]);

  const getTotalTDS = (analysisData) => {
    const inorganicAnalyses = [
      'Fluorid', 'Radon', 'Ammonium', 'Nitrat (NO3)', 'Nitrit (NO2)',
      'Natrium (Na)', 'Klorid', 'Kalcium (Ca)', 'Järn (Fe)', 'Mangan (Mn)',
      'Arsenik (As)', 'Uran (U)'
    ];

    const organicAnalyses = [
      'COD-Mn', 'Odlingsbara mikroorganismer', 'Escherichia coli', 'Koliforma bakterier'
    ];

    const allAnalyses = [...inorganicAnalyses, ...organicAnalyses];

    return allAnalyses.reduce((total, analysisName) => {
      const analysis = analysisData.find(item => item.Analys === analysisName);
      const resultValue = analysis ? parseFloat(analysis.Resultat) : 0;
      return total + (isNaN(resultValue) ? 0 : resultValue);
    }, 0);
  };

  const getRecommendedProducts = useCallback((data, productData, thresholds) => {
    if (!probableFlow || probableFlow <= 0) {
      return [];
    }

    const feResult = data.find(item => item.Analys === 'Järn (Fe)')?.Resultat || 0;
    const mnResult = data.find(item => item.Analys === 'Mangan (Mn)')?.Resultat || 0;
    const isFeAboveThreshold = feResult > thresholds['Järn (Fe)'];
    const isMnAboveThreshold = mnResult > thresholds['Mangan (Mn)'];

    const shouldExcludePHAndCO2Products = isFeAboveThreshold || isMnAboveThreshold;

    const doesFilterBedMatch = (analysisData, filterBed) => {
      const findAnalysisResult = (analysisName) => {
        const analysis = analysisData.find(a => a.Analys === analysisName);
        return analysis ? parseFloat(analysis.Resultat) : NaN;
      };

      const pHResult = findAnalysisResult('pH');
      const alkalinityResult = findAnalysisResult('Alkalinitet');
      const dHResult = findAnalysisResult('Hårdhet');
      const FeResult = findAnalysisResult('Järn (Fe)');
      const MnResult = findAnalysisResult('Mangan (Mn)');
      const FeMnResult = FeResult + MnResult;

      if (!isNaN(pHResult) && !isNaN(alkalinityResult) && !isNaN(dHResult) && !isNaN(FeResult) && !isNaN(MnResult)) {
        if (
          (pHResult < 7 && alkalinityResult > 60 && dHResult < 5 && FeMnResult > 3.5 && filterBed.name.includes("Semidol")) ||
          (pHResult >= 7 && pHResult <= 8 && alkalinityResult < 60 && dHResult > 8 && FeMnResult > 2 && filterBed.name.includes("HC")) ||
          (pHResult > 7 && alkalinityResult < 60 && dHResult > 15 && FeMnResult < 2 && filterBed.name.includes("HK"))
        ) {
          return true;
        }
      }
      return false;
    };

    const findSuitableFilterBed = (analysisData, product, suitableModelCapacity) => {
      if (!analysisData || !Array.isArray(analysisData)) return null;

      const suitableModel = product.Modeller.find(model => {
        const modelCapacity = extractModelCapacity(model.Modell);
        return modelCapacity === suitableModelCapacity;
      });

      if (!suitableModel || !Array.isArray(suitableModel.Filterbäddar)) return null;

      if (suitableModel.Filterbäddar.length === 1) {
        const filterBed = suitableModel.Filterbäddar[0];
        const volumeMatch = filterBed.name.match(/\((\d+)L\)/);
        if (volumeMatch) {
          const volumePerUnit = parseInt(volumeMatch[1], 10);
          const modelVolume = extractModelCapacity(suitableModel.Modell);
          const quantityRequired = Math.ceil(modelVolume / volumePerUnit);
          return {
            ...filterBed,
            quantity: quantityRequired,
            totalPrice: filterBed.Grundpris * quantityRequired
          };
        }
        else {
          return {
            ...filterBed,
            totalPrice: filterBed.Grundpris
          };
        }
      }

      for (const filterBed of suitableModel.Filterbäddar) {
        if (doesFilterBedMatch(analysisData, filterBed)) {
          const volumeMatch = filterBed.name.match(/\((\d+)L\)/);
          if (volumeMatch) {
            const volumePerUnit = parseInt(volumeMatch[1], 10);
            const modelVolume = extractModelCapacity(suitableModel.Modell);
            const quantityRequired = Math.ceil(modelVolume / volumePerUnit);
            return {
              ...filterBed,
              quantity: quantityRequired,
              totalPrice: filterBed.Grundpris * quantityRequired
            };
          }
          else {
            return {
              ...filterBed,
              totalPrice: filterBed.Grundpris
            };
          }
        }
      }

      return null;
    };

    const natriumValue = getNatriumValue();
    const conductivityProblematic = isConductivityProblematic();
    const stainlessSteelProducts = ["Ferroline", "Göingefilter Kombi", "Göingefilter Kombi AUL", "Alkalite-R", "Göingefilter K", "Carbo", "ARSENIC R", "WEDECO AQUADA"];

    return Object.values(productData).reduce((acc, product) => {
      let hasSolution = false;
      let extraProblemSolution = [];
      let suitableModel = null;

      if (conductivityProblematic && stainlessSteelProducts.includes(product.Product)) {
        return acc;
      }

      if (product.Product === "WEDECO AQUADA") {
        const bacterialIssues = ['Koliforma bakterier', 'Escherichia coli', 'Odlingsbara mikroorganismer'];
        const bacteriaValues = bacterialIssues.map(issue => {
          const analysis = data.find(a => a.Analys === issue);
          return analysis ? parseFloat(analysis.Resultat) : null;
        });

        const bacterialProblems = bacteriaValues.map((value, index) =>
          value !== null && value > thresholds[bacterialIssues[index]]
        );

        if (bacterialProblems.some(isProblematic => isProblematic)) {
          let added = false;
          for (let index = 0; index < product.Modeller.length; index++) {
            const model = product.Modeller[index];
            const modelFlowCapacity = getModelFlowCapacity(model.Specifikationer, probableFlow, data);

            if (!added && modelFlowCapacity !== null && probableFlow <= modelFlowCapacity) {
              const operatingCosts = calculateAnnualOperatingCosts(model, probableFlow, totalHardness, nitrateLevel);
              acc.push({
                ...product,
                Modeller: [{ ...model, operatingCosts }],
              });
              added = true;
            } else if (added) {
              if (modelFlowCapacity !== null && probableFlow <= modelFlowCapacity) {
                const operatingCosts = calculateAnnualOperatingCosts(model, probableFlow, totalHardness, nitrateLevel);
                acc[acc.length - 1].Modeller.push({ ...model, operatingCosts });
                break;
              }
            }
          }
        }
        return acc;
      }

      if (product.Product === "Alterline") {
        const pHAnalysis = data.find(a => a.Analys === 'pH');
        const pHValue = pHAnalysis ? parseFloat(pHAnalysis.Resultat) : null;
        const CO2Analysis = data.find(a => a.Analys === 'Marmoraggressiv kolsyra');
        const CO2Value = CO2Analysis ? parseFloat(CO2Analysis.Resultat) : null;

        const isPHProblem = pHValue !== null && (pHValue < thresholds["pH"] - 0.5 || pHValue > thresholds["pH"] + 0.5);
        const isCO2Problem = CO2Value !== null && CO2Value > thresholds["Marmoraggressiv kolsyra"];

        if (isFeAboveThreshold || isMnAboveThreshold) {
          return acc;
        }

        if (isPHProblem || isCO2Problem) {
          acc.push({
            ...product
          });
        }
        return acc;
      }

      if (shouldExcludePHAndCO2Products) {
        const onlyPHAndCO2 = product.Problemlösning.every(p => ['pH', 'Marmoraggressiv kolsyra'].includes(p.name));
        if (onlyPHAndCO2) {
          return acc;
        }
      }

      if (product.Product === "M25 - M500" && natriumValue > 400) {
        return acc;
      }
      if (product.Product === "Crystal Right (CR)" && natriumValue > 200) {
        return acc;
      }

      if (product.Product === "Crystal Right (CR)") {
        const allProblems = new Set([
          ...product.Problemlösning.map(p => p.name),
          ...product.Modeller.flatMap(model => model.Extra_Problemlösning.map(p => p.name))
        ]);

        const crSolutions = data.filter(analysis => {
          const resultValue = getResultValue(analysis.Resultat);
          const thresholdValue = thresholds[analysis.Analys];
          return resultValue !== null && thresholdValue !== undefined &&
            isConditionMet(analysis.Analys, resultValue, thresholdValue) &&
            allProblems.has(analysis.Analys);
        }).map(analysis => analysis.Analys);

        hasSolution = crSolutions.length > 0;

        if (hasSolution) {
          extraProblemSolution = product.Modeller.map(model => {
            const matchedExtraProblems = model.Extra_Problemlösning.filter(p => crSolutions.includes(p.name));
            const modelFlowCapacity = getModelFlowCapacity(model.Specifikationer, probableFlow, analysisData);
            if (modelFlowCapacity !== null && probableFlow <= modelFlowCapacity) {
              return {
                ...model,
                Extra_Problemlösning: matchedExtraProblems,
                operatingCosts: calculateAnnualOperatingCosts(model, probableFlow, totalHardness, nitrateLevel)
              };
            } else {
              return null;
            }
          }).filter(model => model !== null);
        }
      }

      if (product.Product === "RO MO" || product.Product === "Robust") {
        const maxTDS = product.Product === "RO MO" ? 3000 : 1500;
        const totalTDS = getTotalTDS(data);

        if (totalTDS <= maxTDS) {
          const problemSolutions = product.Problemlösning.map(p => p.name);
          hasSolution = data.some(analysis => {
            const resultValue = getResultValue(analysis.Resultat);
            const thresholdValue = thresholds[analysis.Analys];
            return resultValue !== null && thresholdValue !== undefined &&
              isConditionMet(analysis.Analys, resultValue, thresholdValue) &&
              problemSolutions.includes(analysis.Analys);
          });

          if (hasSolution) {
            const suitableModels = product.Modeller.filter(model =>
              getModelFlowCapacity(model.Specifikationer, probableFlow, analysisData)
            ).map(model => ({
              ...model,
              operatingCosts: calculateAnnualOperatingCosts(model, probableFlow, totalHardness, nitrateLevel)
            }));

            if (suitableModels.length) {
              acc.push({
                ...product,
                Modeller: suitableModels
              });
            }
          }
        }
      } else {
        hasSolution = data.some(analysis => {
          if (!analysis.Resultat) return false;
          const resultValue = getResultValue(analysis.Resultat);
          const thresholdValue = thresholds[analysis.Analys];
          return resultValue !== null && thresholdValue !== undefined &&
            isConditionMet(analysis.Analys, resultValue, thresholdValue) &&
            product.Problemlösning.some(p => p.name === analysis.Analys);
        });
      }

      if (hasSolution) {
        const sortedModels = (extraProblemSolution.length > 0 ? extraProblemSolution : product.Modeller)
          .sort((a, b) => getModelFlowCapacity(a.Specifikationer, probableFlow, analysisData) - getModelFlowCapacity(b.Specifikationer, probableFlow, analysisData));

        suitableModel = sortedModels.find(model => {
          const modelFlowCapacity = getModelFlowCapacity(model.Specifikationer, probableFlow, analysisData);
          return modelFlowCapacity !== null && probableFlow <= modelFlowCapacity;
        });
        if (suitableModel) {
          const modelCapacity = extractModelCapacity(suitableModel.Modell);
          const suitableFilterBed = findSuitableFilterBed(data, product, modelCapacity);
          const operatingCosts = calculateAnnualOperatingCosts(suitableModel, probableFlow, totalHardness, nitrateLevel);

          acc.push({
            ...product,
            Modeller: [{ ...suitableModel, operatingCosts }],
            ...(suitableFilterBed && { recommendedFilterBed: suitableFilterBed })
          });
        }
      }
      return acc;
    }, []);
  }, [probableFlow, totalHardness, nitrateLevel, analysisData, getNatriumValue, isConductivityProblematic]);

  useEffect(() => {
    setAnalysisData(data);
  }, [data]);

  useEffect(() => {
    setRecommendedProducts(getRecommendedProducts(analysisData, productData, thresholdData));
  }, [analysisData, getRecommendedProducts, productData, thresholdData]);

  const handleChangeFlowValue = useCallback(() => {
    probableFlow > 0 ? setShowFlowReminder(false) : setShowFlowReminder(true);
  }, [probableFlow, setShowFlowReminder]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const newHasResultValue = data.some(result => result.Resultat);

      if (!newHasResultValue) {
        const newHasResultValue = data.some(result => result.Resultat);
        if (newHasResultValue) {
          setShowFlowReminder(true);
        }
      }
    }, 100);

    return () => clearInterval(intervalId);
  }, [data]);

  useEffect(() => {
    handleChangeFlowValue();
  }, [probableFlow, handleChangeFlowValue]);

  const handleFlowChange = (value) => {
    setProbableFlow(value);
  };

  const handleParameterValueChange = useCallback((record, newValue) => {
    setAnalysisData(currentAnalysisData =>
      currentAnalysisData.map(item =>
        item.key === record.key ? { ...item, Resultat: newValue } : item
      )
    );
  }, []);

  const columns = useMemo(() => [
    {
      title: 'Analys',
      dataIndex: 'Analys',
      key: 'Analys',
    },
    {
      title: 'Resultat',
      dataIndex: 'Resultat',
      key: 'Resultat',
      render: (text, record) => {
        const handleChange = (e) => {
          const newValue = record.Analys === 'Lukt' ? e.target.checked.toString() : e.target.value;
          handleParameterValueChange(record, newValue);
        };

        if (record.Analys === 'Lukt') {
          return (
            <Checkbox
              checked={record.Resultat === 'true'}
              onChange={handleChange}
            />
          );
        } else {
          const resultValue = parseFloat(record.Resultat);
          const thresholdValue = thresholdData[record.Analys];

          let isProblematic = false;
          if (record.Analys === 'pH') {
            isProblematic = !isNaN(resultValue) && resultValue < thresholdValue;
          } else {
            isProblematic = !isNaN(resultValue) && resultValue > thresholdValue;
          }

          return (
            <Input
              value={record.Resultat}
              onChange={handleChange}
              style={{ backgroundColor: isProblematic ? '#ffcccc' : 'white' }}
            />
          );
        }
      },
    },
    {
      title: 'Enhet',
      dataIndex: 'Enhet',
      key: 'Enhet',
    },
  ], [handleParameterValueChange, thresholdData]);

  const mergedColumns = columns.map((col) => ({
    ...col,
    onCell: (record) => ({
      record,
      editable: col.editable,
      title: col.title,
    }),
  }));

  const onChange = info => {
    const { file } = info;

    if (file.status === 'removed') {
      setData(initialData.map((item, index) => ({
        ...item,
        key: item.id || `data-${index}`,
      })));
      setAnalysisData(initialData.map((item, index) => ({
        ...item,
        key: item.id || `data-${index}`,
      })));
      setError(null);
    } else if (file.originFileObj || file.status !== 'removed') {
      handleFileChange(file.originFileObj || file, setAnalysisData, setError);
    }
  };

  const fileProps = {
    name: 'file',
    multiple: false,
    beforeUpload: file => {
      return false;
    },
    onChange: onChange,
    accept: "application/pdf",
    onDrop(e) {
      console.log('Dropped files', e.dataTransfer.files);
    },
    locale: {
      removeFile: 'Radera fil'
    },
    showUploadList: {
      removeIcon: <DeleteOutlined />,
    },
  };

  const presentProblemsData = recommendedProducts.map(product => {
    return {
      ...product,
      Problemlösning: product.Problemlösning.filter(p => {
        const analysis = analysisData.find(a => a.Analys === p.name);
        if (!analysis || !analysis.Resultat) return false;
        const resultValue = getResultValue(analysis.Resultat);
        const thresholdValue = thresholdData[p.name];
        return isConditionMet(p.name, resultValue, thresholdValue);
      })
    };
  });

  return (
    <Form form={form} component={false}>
      <Divider />
      <Title level={4}>Sannolikt flöde</Title>
      <InputNumber
        min={0}
        value={probableFlow}
        onChange={handleFlowChange}
        placeholder="Sannolikt flöde"
        style={{ marginBottom: 16 }}
        addonAfter="l/min"
      />
      {showFlowReminder && (
        <p style={{ color: 'red' }}>
          Vänligen fyll i det sannolika flödet först.
        </p>
      )}
      <div style={{ marginBottom: 16 }}>
        {error && <div style={{ color: 'red', marginBottom: '10px' }}>{error}</div>}
        <Dragger {...fileProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Klicka eller dra filen till detta område för att ladda upp</p>
          <p className="ant-upload-hint">
            Stöd för inläsning av analysrapport från Eurofins i PDF-format.
          </p>
        </Dragger>
      </div>
      <Table
        dataSource={analysisData}
        columns={mergedColumns}
        rowClassName="editable-row"
        pagination={false}
        size="middle"
        bordered
        scroll={{ x: '100%', y: 500 }}
      />
      <Quote
        products={recommendedProducts}
        presentProblems={presentProblemsData}
        likelyFlow={probableFlow}
        totalHardness={totalHardness}
        nitrateLevel={nitrateLevel}
        getModelFlowCapacity={getModelFlowCapacity}
      />
    </Form>
  );
};

export default WaterQualityTable;
